/**
 * Reads an image src into a `data:` URI.
 *
 * This is the read `useImagesEdit` used to perform eagerly on every image
 * click — moved to the one place that actually needs the pixels: the crop
 * step. Cropping draws the image onto a canvas and calls `toDataURL`, which
 * throws a SecurityError if the canvas was tainted by a cross-origin source,
 * so the bytes have to be inlined same-origin first.
 *
 * Resizing needs none of this (it's a CSS transform plus a `scale` query
 * param), which is why opening the edit modal no longer waits on it.
 */
export const loadImageAsDataUri = async (src: string): Promise<string> => {
  const response = await fetch(src);
  if (!response.ok) {
    throw new Error(`Failed to load image for editing: ${response.status}`);
  }
  const blob = await response.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result?.toString();
      if (result) {
        resolve(result);
      } else {
        reject(new Error("Failed to read image for editing"));
      }
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read image for editing"));
    reader.readAsDataURL(blob);
  });
};
